import React, { useEffect } from "react";
import {
    Toaster,
    useToastController,
    useId,
    Toast,
    ToastTitle,
    ToastBody,
} from "@fluentui/react-components";

/**
 * In-app notification banners.
 *
 * The portal previously reported every error through the browser's native
 * `alert()`. Native dialogs render outside the page, so they are invisible to
 * (and unreadable by) automated/agent-driven browsers, which made failures look
 * like dead buttons — e.g. a wrong password on the login screen appeared to do
 * nothing at all. They also block the JS thread and can't be styled.
 *
 * Mounting this component installs a toaster and routes `window.alert` through
 * it, so all existing call sites become in-app banners with no per-site edits.
 * New code should call `notify()` directly instead of `alert()`.
 */

const TOASTER_ID = "wh-app-toaster";

// Set by AppNotifications once mounted; used by the notify() helper below.
let dispatchToast = null;

/**
 * Show an in-app notification banner.
 * @param {string} message - body text
 * @param {"error"|"success"|"warning"|"info"} intent - banner styling
 * @param {string} [title] - optional heading; defaults per intent
 */
export function notify(message, intent = "error", title) {
    const text = typeof message === "string" ? message : String(message ?? "");
    if (!text) return;

    const defaultTitles = {
        error: "Something went wrong",
        success: "Success",
        warning: "Heads up",
        info: "Notice",
    };

    // Before mount (or if the provider is missing) fall back to the native
    // dialog so a message is never silently swallowed.
    if (!dispatchToast) {
        // eslint-disable-next-line no-alert
        window.__nativeAlert ? window.__nativeAlert(text) : console.warn(text);
        return;
    }

    dispatchToast(
        <Toast>
            <ToastTitle>{title || defaultTitles[intent] || defaultTitles.info}</ToastTitle>
            <ToastBody>{text}</ToastBody>
        </Toast>,
        { intent, timeout: intent === "error" ? 8000 : 4000 }
    );
}

export default function AppNotifications() {
    const toasterId = useId(TOASTER_ID);
    const { dispatchToast: dispatch } = useToastController(toasterId);

    useEffect(() => {
        dispatchToast = dispatch;

        // Route legacy alert() calls (126 of them across the app) into banners.
        // Keep a handle on the native one for the pre-mount fallback above.
        if (!window.__nativeAlert) {
            window.__nativeAlert = window.alert.bind(window);
        }
        window.alert = (msg) => notify(msg, "error");

        return () => {
            if (window.__nativeAlert) window.alert = window.__nativeAlert;
            dispatchToast = null;
        };
    }, [dispatch]);

    return <Toaster toasterId={toasterId} position="top-end" pauseOnHover />;
}
